/**
 * Pull-to-refresh, as arithmetic and a state machine.
 *
 * WHY THE APP NEEDS ONE AT ALL, given every browser has it. Run from the home screen this is a
 * standalone window: no address bar, no reload button, and no native pull gesture. Nine managers
 * were told to add it to their home screen, which means nine managers have no way to reload the
 * app at all - and on 25 August that mattered twice in one day. A deploy went out and the phone
 * kept serving the previous build, because the service worker only swaps on a real page load.
 *
 * SO A REFRESH HERE IS A RELOAD, not a re-fetch. The Firestore store is subscribed with
 * `onSnapshot` and is already live; there is no stale data to go and get. What goes stale is the
 * APP - the bundle the worker handed over last time - so pulling asks the worker to check for a
 * new one and then reloads into it. Anything else would be an animation that does nothing.
 *
 * The numbers are here rather than in the component so they can be tested in node. The gesture is
 * the part that is easy to get subtly wrong: a threshold that fires on a scroll flick, or a pull
 * that keeps tracking the finger after the user has clearly changed his mind.
 */

/**
 * How far the finger must travel, AFTER damping, before releasing will refresh.
 *
 * TIM'S ASK, TWICE. Tripled from 72 on 25 August, then ×1.2 again — 72 → 216 → 259. A refresh
 * here is a full page reload: it is the only way nine home-screen managers can pick up a deploy,
 * and it is also the one gesture in this app that throws away whatever is on screen. At 72 it
 * armed after about 106px of finger travel, which is inside the range of an ordinary flick at the
 * top of a long page. At 259 it takes a deliberate drag of about **382px** — far enough that
 * nobody arrives there by accident, and the indicator has the whole way down to say so.
 *
 * `MAX_PULL` moves with it by the same factor, and that pairing is not incidental — see below.
 */
export const THRESHOLD = 259;

/**
 * The indicator stops moving here however hard the pull, so it cannot be dragged off-screen.
 *
 * KEEP THIS AT ROUGHLY 1.53× `THRESHOLD` — the original 110/72. The resistance curve is written
 * in terms of `MAX_PULL`, so scaling the two together scales it exactly: the length of the
 * gesture changes and nothing about how it feels along the way does. Moving one without the other
 * reshapes the curve, and moving the threshold alone can put it beyond anything `damp` returns,
 * which arms nothing at all however hard the page is pulled.
 *
 * It is now a CEILING RATHER THAN A STOP. The bungee below approaches it asymptotically, so the
 * indicator gets to about 374 and never quite here.
 */
export const MAX_PULL = 396;

/**
 * How much of the room past the threshold the bungee spends before it stops giving.
 *
 * Tim, 26 August: *"a little more bungee resistance as it approaches the bottom."* Below the
 * threshold nothing changed — the arming distance is a promise the indicator makes and this must
 * not move it — so the extra resistance lives entirely in the overshoot, where the gesture has
 * already been decided and the only thing left to communicate is that it is at the end of its
 * travel. Smaller is stiffer.
 */
const BUNGEE = 0.55;

/**
 * A pull moves the page less than it moves the finger, and less and less the further it goes.
 *
 * That resistance is not decoration: it is the only thing telling somebody mid-gesture that this
 * is a pull rather than a scroll, on a surface with no other feedback. Linear tracking feels like
 * the page has come loose.
 */
export function damp(raw: number): number {
  if (raw <= 0) return 0;
  /*
   * The parabola `raw · (1 − raw / 3·MAX)` is the resistance itself, and it is what every real
   * pull lives on: it arms at 0.97·MAX, well short of its own peak at 1.5·MAX.
   *
   * PAST THAT PEAK IT TURNS OVER, which is what the old `Math.min(…, 0.6)` was there to catch —
   * beyond 1.8·MAX the reduction stopped growing and the curve went linear again. But those two
   * branches do not meet: the parabola is already falling when the linear tail picks it up, so
   * between 1.5·MAX and 1.875·MAX the result DECREASED as the finger kept going down. At the old
   * numbers that dip was 3px and nobody could see it. Tripled, it is 10px of the page creeping
   * back up under a finger that is still pulling, which reads as the gesture breaking.
   *
   * So the peak is held until the linear tail genuinely overtakes it. Monotonic everywhere, and
   * identical to the old shape below 1.5·MAX, which is all of the gesture anybody performs.
   */
  const knee = MAX_PULL * 1.5;
  const eased = raw <= knee
    ? raw * (1 - raw / (MAX_PULL * 3))
    : Math.max(knee * (1 - knee / (MAX_PULL * 3)), raw * 0.4);
  const capped = Math.min(eased, MAX_PULL);

  /*
   * THE BUNGEE, and it applies only ABOVE the threshold.
   *
   * That boundary is the whole design of it. Everything below the threshold is the part of the
   * gesture that decides the outcome, and the distance to arming is a promise the dial is making
   * as it fills — bending the curve there would move the arming point and make the dial a liar.
   * Above it the decision is made, and the only thing left to say is "this is the end of the
   * travel", which is what a rope going tight says better than a hard stop does.
   *
   * Exponential, so it eases towards `MAX_PULL` and never reaches it. There is no point at which
   * the page simply refuses to move any further, which is the thing that feels broken.
   */
  if (capped <= THRESHOLD) return capped;
  const room = MAX_PULL - THRESHOLD;
  return THRESHOLD + room * (1 - Math.exp(-(capped - THRESHOLD) / (room * BUNGEE)));
}

/* -- colour ------------------------------------------------------------------------------------
 *
 * Tim's two schools, and the gesture is the journey between them: Maryland red at the top of the
 * pull, Virginia Tech maroon by the time it arms, and a blue-shifted maroon the moment it does.
 *
 * The ramp is computed here rather than in CSS for two reasons. It is testable in node, which a
 * `color-mix()` in a stylesheet is not; and the armed colour is a SWITCH rather than a further
 * step along the same ramp, which is the point — the last thing that happens before you let go
 * should not look like more of what was already happening.
 */

/** Maryland red, where the pull starts. */
export const MARYLAND = '#e21833';
/** Virginia Tech maroon, where it arrives. */
export const VT_MAROON = '#861f41';
/** The same maroon with its hue carried round to blue-violet: armed, and unmistakably a change. */
export const ARMED = '#401f85';

const hex = (h: string): [number, number, number] => [
  parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16),
];
const pad = (n: number) => Math.round(n).toString(16).padStart(2, '0');

/**
 * The indicator's colour at a given fraction of the way to arming.
 *
 * `armed` is deliberately not `pct >= 1`: the phase is the authority on whether releasing will
 * reload, and the colour has to agree with the phase rather than re-derive it from a number that
 * is clamped at 1 and would also be 1 for the whole of the overshoot.
 */
export function ink(pct: number, armed = false): string {
  if (armed) return ARMED;
  const t = Math.min(Math.max(pct, 0), 1);
  const a = hex(MARYLAND);
  const b = hex(VT_MAROON);
  return `#${a.map((v, i) => pad(v + (b[i]! - v) * t)).join('')}`;
}

export type PullPhase = 'idle' | 'pulling' | 'armed' | 'refreshing';

export interface PullState {
  phase: PullPhase;
  /** Where the finger went down. Meaningless unless pulling. */
  startY: number;
  /** Damped, so this is what the indicator should be offset by. */
  distance: number;
}

export const IDLE: PullState = { phase: 'idle', startY: 0, distance: 0 };

/**
 * Whether a touch starting here may begin a pull.
 *
 * Four refusals, and each one is a gesture that would otherwise be stolen from the user:
 *
 *   NOT AT THE TOP    - anywhere else the gesture is a scroll, and always was.
 *   TYPING            - a half-written rule proposal is the most expensive thing in this app to
 *                       lose, and a reload loses it. If a field has focus, the answer is no.
 *   INSIDE A SCROLLER - the draft board and the wide tables scroll inside themselves. A pull
 *                       that started in one of those is somebody trying to move that, not the page.
 *   OWNED             - the touch began on a surface that handles its own drag (`touch-action:
 *                       none`: the photo crop stage) or inside a dialog or sheet. Tim, 4 September:
 *                       dragging the crop on a phone pulled the page under the sheet instead of
 *                       the picture. Nothing inside a modal is ever a request to reload.
 *   ALREADY BUSY      - a second pull while the first is reloading is not a second reload.
 */
export interface StartContext {
  scrollY: number;
  /** True when a text field, textarea or contenteditable has focus. */
  typing: boolean;
  /** True when the touch began inside an element that scrolls on its own. */
  inScroller: boolean;
  /** True when the touch began on a surface that owns the gesture: a drag stage, a dialog, a sheet. */
  owned?: boolean;
  phase: PullPhase;
}

export function canStart(c: StartContext): boolean {
  return c.scrollY <= 0 && !c.typing && !c.inScroller && !c.owned
    && (c.phase === 'idle' || c.phase === 'pulling');
}

export function begin(y: number): PullState {
  return { phase: 'pulling', startY: y, distance: 0 };
}

/**
 * The finger moved.
 *
 * A pull that goes back above where it started is abandoned rather than clamped at zero - the
 * user has scrolled up past the top and come back down, and continuing to treat that as a pull
 * would re-arm on the way past. `dx` abandons a gesture that is mostly sideways, which on a phone
 * is somebody swiping a wide table underneath a page that happens to be at the top.
 */
export function move(state: PullState, y: number, dx = 0): PullState {
  if (state.phase !== 'pulling' && state.phase !== 'armed') return state;
  const raw = y - state.startY;
  if (raw <= 0) return IDLE;
  if (Math.abs(dx) > raw) return IDLE;
  const distance = damp(raw);
  return { ...state, phase: distance >= THRESHOLD ? 'armed' : 'pulling', distance };
}

/** Releasing. Only an armed pull refreshes; anything else springs back. */
export function release(state: PullState): PullState {
  if (state.phase === 'armed') return { phase: 'refreshing', startY: 0, distance: THRESHOLD };
  return IDLE;
}

/** What the indicator says. The wording is the affordance — see `PullToRefresh.tsx`. */
export function label(phase: PullPhase): string {
  if (phase === 'refreshing') return 'Reloading…';
  if (phase === 'armed') return 'Release to reload';
  return 'Pull to reload';
}
